import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Table, Button, Nav } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../firebase';
import { ref, onValue, set, remove } from 'firebase/database';
import { Car } from '../types/car';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGasPump, faWrench } from '@fortawesome/free-solid-svg-icons';
import styles from './MoschtEvents.module.css';
import { Link, useLocation } from 'react-router-dom';

interface Fill {
  id: string;
  timestamp: number;
  odometer: number;
  fuelamount: number;
  price: number;
}

interface Log {
  id: string;
  timestamp: number;
  what: string;
  who: string;
  odometer: number;
  price: number;
}

interface Event {
  id: string;
  type: 'fill' | 'log';
  timestamp: number;
  odometer: number;
  data: Fill | Log;
}

const MoschtEvents = () => {
  const { currentUser } = useAuth();
  const [ownedCarIds, setOwnedCarIds] = useState<string[]>([]);
  const [cars, setCars] = useState<Record<string, Car>>({});
  const [selectedCar, setSelectedCar] = useState<string>('');
  const [events, setEvents] = useState<Event[]>([]);
  const location = useLocation();

  useEffect(() => {
    if (!currentUser) return;

    const ownedCarsRef = ref(database, `user_settings/${currentUser.uid}/ownedCars`);
    const ownedCarsUnsubscribe = onValue(ownedCarsRef, (snapshot) => {
      const data = snapshot.val() || {};
      setOwnedCarIds(Object.values(data));
    });

    const carsRef = ref(database, 'cars');
    const carsUnsubscribe = onValue(carsRef, (snapshot) => {
      const data = snapshot.val();
      setCars(data || {});
    });

    const selectedCarRef = ref(database, `user_settings/${currentUser.uid}/selectedCar`);
    const selectedCarUnsubscribe = onValue(selectedCarRef, (snapshot) => {
      const data = snapshot.val();
      setSelectedCar(data || '');
    });

    return () => {
      ownedCarsUnsubscribe();
      carsUnsubscribe();
      selectedCarUnsubscribe();
    };
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || !selectedCar) {
      setEvents([]);
      return;
    }

    const fillsRef = ref(database, `cars/${selectedCar}/fills`);
    const logsRef = ref(database, `cars/${selectedCar}/logs`);

    const fillsUnsubscribe = onValue(fillsRef, (snapshot) => {
      const fillsData = snapshot.val();
      let fillEvents: Event[] = [];
      
      if (fillsData) {
        fillEvents = Object.entries(fillsData).map(([id, fill]: [string, any]) => ({
          id,
          type: 'fill' as const,
          timestamp: Number(fill.timestamp),
          odometer: Number(fill.odometer),
          data: {
            id,
            timestamp: Number(fill.timestamp),
            odometer: Number(fill.odometer),
            fuelamount: Number(fill.fuelamount),
            price: Number(fill.price)
          }
        }));
      }

      const logsUnsubscribe = onValue(logsRef, (snapshot) => {
        const logsData = snapshot.val();
        let logEvents: Event[] = [];

        if (logsData) {
          logEvents = Object.entries(logsData).map(([id, log]: [string, any]) => ({
            id,
            type: 'log' as const,
            timestamp: Number(log.timestamp),
            odometer: Number(log.odometer),
            data: {
              id,
              timestamp: Number(log.timestamp),
              odometer: Number(log.odometer),
              what: log.what,
              who: log.who,
              price: Number(log.price)
            }
          }));
        }

        const allEvents = [...fillEvents, ...logEvents].sort((a, b) => b.odometer - a.odometer);
        setEvents(allEvents);
      });

      return () => {
        logsUnsubscribe();
      };
    });

    return () => {
      fillsUnsubscribe();
    };
  }, [currentUser, selectedCar]);

  const handleCarSelect = async (carId: string) => {
    if (!currentUser) return;
    
    try {
      const selectedCarRef = ref(database, `user_settings/${currentUser.uid}/selectedCar`);
      await set(selectedCarRef, carId);
      setSelectedCar(carId);
    } catch (error) {
      console.error('Error setting selected car:', error);
    }
  };

  // Filter owned cars to only show active ones
  const activeCars = ownedCarIds
    .filter(id => cars[id] && cars[id].is_active)
    .map(id => ({
      id,
      ...cars[id]
    }));

  return (
    <Container>
      <h2 className="mb-4">Moscht Ereignisse</h2>
      
      {/* Navigation Tabs */}
      <Nav variant="tabs" className="mb-4">
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht" 
            active={location.pathname === '/dashboard/moscht'}
          >
            Dashboard
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/tankungen" 
            active={location.pathname === '/dashboard/moscht/tankungen'}
          >
            Tankungen
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/events" 
            active={location.pathname === '/dashboard/moscht/events'}
          >
            Events
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/logs" 
            active={location.pathname === '/dashboard/moscht/logs'}
          >
            Logs
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/cars" 
            active={location.pathname === '/dashboard/moscht/cars'}
          >
            Fahrzeuge
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/settings" 
            active={location.pathname === '/dashboard/moscht/settings'}
          >
            Konfiguration
          </Nav.Link>
        </Nav.Item>
      </Nav>
      
      <Card className="mb-4">
        <Card.Body>
          <Card.Title style={{ color: '#1780a6' }}>Fahrzeugauswahl</Card.Title>
          <Form className="mt-3">
            <Form.Group>
              <Form.Label>Aktuelles Fahrzeug</Form.Label>
              <Form.Select 
                value={selectedCar} 
                onChange={(e) => handleCarSelect(e.target.value)}
                className="mb-3"
              >
                <option value="">Fahrzeug auswählen...</option>
                {activeCars.map(car => (
                  <option key={car.id} value={car.id}>
                    {car.name}
                  </option>
                ))}
              </Form.Select>
              {!activeCars.length && (
                <div className="text-muted">
                  Keine aktiven Fahrzeuge verfügbar. Bitte fügen Sie zuerst Fahrzeuge in Ihrem Profil hinzu.
                </div>
              )}
            </Form.Group>
          </Form>
        </Card.Body>
      </Card>

      {selectedCar && events.length > 0 && (
        <div className={styles.timeline}>
          {events.map((event, index) => (
            <div 
              key={`${event.type}-${event.id}`} 
              className={`${styles.timelineElement} ${index % 2 === 0 ? styles.left : styles.right}`}
            >
              <div className={`${styles.timelineContent} ${event.type === 'fill' ? styles.fillContent : styles.maintenanceContent}`}>
                <div className={styles.timelineDate}>
                  {new Intl.DateTimeFormat('de-DE', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  }).format(event.timestamp)}
                </div>
                <div className={styles.odometer}>
                  {event.odometer.toLocaleString('de-CH')} km
                </div>
                {event.type === 'fill' ? (
                  <div className={styles.details}>
                    <strong>Tankfüllung</strong><br />
                    Menge: {(event.data as Fill).fuelamount} l<br />
                    Preis: {(event.data as Fill).price.toLocaleString('de-CH', { minimumFractionDigits: 2 })} CHF
                  </div>
                ) : (
                  <div className={styles.details}>
                    <strong>Wartung</strong><br />
                    {(event.data as Log).what}<br />
                    Durchgeführt von: {(event.data as Log).who}<br />
                    Kosten: {(event.data as Log).price.toLocaleString('de-CH', { minimumFractionDigits: 2 })} CHF
                  </div>
                )}
              </div>
              <div className={`${styles.timelineIcon} ${event.type === 'fill' ? styles.fillIcon : styles.maintenanceIcon}`}>
                {event.type === 'fill' ? 
                  <FontAwesomeIcon icon={faGasPump} /> : 
                  <FontAwesomeIcon icon={faWrench} />
                }
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedCar && !events.length && (
        <div className="text-muted text-center mt-4">
          Keine Ereignisse vorhanden.
        </div>
      )}
    </Container>
  );
};

export default MoschtEvents;
